import type { SubscriptionPlan } from '../types';

export type BillingInterval = 'month' | 'year';

export interface PlanDefinition {
  id: SubscriptionPlan;
  name: string;
  tagline: string;
  /** Monthly price in GBP. 0 for free. */
  monthly: number;
  /** Yearly price in GBP (already discounted). 0 for free. */
  yearly: number;
  features: string[];
  limits: {
    links: number | null;
    clicksPerMonth: number | null;
    apiPerDay: number | null;
  };
  qr: 'none' | 'png' | 'png-svg';
  api: boolean;
  customDomains: boolean;
  highlight?: boolean;
}

export const PLAN_ORDER: SubscriptionPlan[] = ['starter', 'pro', 'growth', 'business'];

export const PLANS: PlanDefinition[] = [
  {
    id: 'starter',
    name: 'Starter',
    tagline: 'For trying it out',
    monthly: 0,
    yearly: 0,
    features: ['100 links', '1,000 clicks / month', 'Basic analytics'],
    limits: { links: 100, clicksPerMonth: 1000, apiPerDay: 0 },
    qr: 'none',
    api: false,
    customDomains: false,
  },
  {
    id: 'pro',
    name: 'Pro',
    tagline: 'For creators',
    monthly: 9.99,
    yearly: 95.9,
    features: ['1,000 links', '50,000 clicks / month', 'Advanced analytics', 'QR codes (PNG)', 'API access'],
    limits: { links: 1000, clicksPerMonth: 50000, apiPerDay: 50000 },
    qr: 'png',
    api: true,
    customDomains: false,
    highlight: true,
  },
  {
    id: 'growth',
    name: 'Growth',
    tagline: 'For teams',
    monthly: 49.99,
    yearly: 479.9,
    features: ['20,000 links', '200,000 clicks / month', 'QR codes (PNG & SVG)', 'API access', 'Team collaboration'],
    limits: { links: 20000, clicksPerMonth: 200000, apiPerDay: 200000 },
    qr: 'png-svg',
    api: true,
    customDomains: false,
  },
  {
    id: 'business',
    name: 'Business',
    tagline: 'For companies',
    monthly: 99.99,
    yearly: 959.9,
    features: ['Unlimited links & clicks', 'Custom domains', 'Priority support', 'Team collaboration', 'SSO'],
    limits: { links: null, clicksPerMonth: null, apiPerDay: null },
    qr: 'png-svg',
    api: true,
    customDomains: true,
  },
];

export const planById = (id?: SubscriptionPlan | string | null): PlanDefinition =>
  PLANS.find((p) => p.id === id) ?? PLANS[0];

export const nextPlan = (id?: SubscriptionPlan | null): PlanDefinition | null => {
  const idx = PLAN_ORDER.indexOf(id ?? 'starter');
  return idx >= 0 && idx < PLAN_ORDER.length - 1 ? planById(PLAN_ORDER[idx + 1]) : null;
};

export const formatPrice = (amount: number, interval: BillingInterval = 'month'): string => {
  if (amount === 0) return 'Free';
  const value = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 2 }).format(
    interval === 'year' ? amount / 12 : amount,
  );
  return value;
};

export const YEARLY_DISCOUNT_PCT = 20;
